import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../ThemeProvider';
import PinPreviewChip from './PinPreviewChip';

function formatTick(v) {
  if (Math.abs(v) >= 100) return Math.round(v).toString();
  if (Math.abs(v) >= 10) return v.toFixed(0);
  return v.toFixed(1);
}

function buildTicks(min, max, count) {
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => min + step * i);
}

function MyPin({ x, y, locked, correct }) {
  return (
    <div style={{
      position: 'absolute',
      left: `${x}%`, top: `${100 - y}%`,
      transform: 'translate(-50%, -50%)',
      animation: locked ? 'none' : 'penPulsePin 1.6s ease-in-out infinite',
      zIndex: 5,
      pointerEvents: 'none',
    }}>
      <div style={{
        position: 'relative', width: 38, height: 38,
        display: 'grid', placeItems: 'center',
      }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 999,
          background: 'var(--c-yellow)',
          border: 'var(--border-ink)',
          boxShadow: 'var(--shadow-hard-sm)',
        }} />
        <div style={{
          position: 'relative',
          fontFamily: 'var(--font-display)', fontSize: 12,
          color: 'var(--c-ink)',
        }}>YOU</div>
      </div>
    </div>
  );
}

function OpponentPin({ pin, revealed }) {
  return (
    <div style={{
      position: 'absolute',
      left: `${pin.x}%`, top: `${100 - pin.y}%`,
      transform: 'translate(-50%, -50%)',
      transition: 'left 600ms cubic-bezier(.5,.1,.3,1), top 600ms cubic-bezier(.5,.1,.3,1)',
      zIndex: 3,
      opacity: revealed ? 0.9 : 0.75,
      pointerEvents: 'none',
      animation: 'penPinDrop 600ms var(--ease-pop) both',
    }}>
      <div style={{
        width: 26, height: 26, borderRadius: 999,
        background: pin.color,
        border: 'var(--border-ink-sm)',
        boxShadow: 'var(--shadow-hard-sm)',
        display: 'grid', placeItems: 'center',
        fontSize: 12,
      }}>{pin.emoji}</div>
    </div>
  );
}

function CorrectMarker({ x, y }) {
  return (
    <div style={{
      position: 'absolute',
      left: `${x}%`, top: `${100 - y}%`,
      zIndex: 6,
      pointerEvents: 'none',
      /* penPinDrop owns the transform (including translate centering) */
      animation: 'penPinDrop 720ms var(--ease-pop) both',
    }}>
      {/* one-time shock-wave ring, centered on this element via left/top 50% */}
      <div style={{
        position: 'absolute', left: '50%', top: '50%',
        width: 28, height: 28, borderRadius: 999,
        border: '2px solid var(--c-green)',
        transform: 'translate(-50%, -50%)',
        animation: 'penRingPulse 850ms var(--ease-snap) 320ms both',
        pointerEvents: 'none',
      }} />
      {/* main 46×46 marker */}
      <div style={{
        width: 46, height: 46,
        position: 'relative',
        display: 'grid', placeItems: 'center',
      }}>
        {/* dashed breathing ring — uses scale only so it stays centred on inset:0 */}
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 999,
          border: '2.5px dashed var(--c-ink)',
          background: 'rgba(24,178,107,0.18)',
          animation: 'penBreath 1.8s ease-in-out infinite',
        }} />
        {/* green dot */}
        <div style={{
          position: 'relative', width: 22, height: 22,
          background: 'var(--c-green)', borderRadius: 999,
          border: '2.5px solid var(--c-ink)',
          boxShadow: 'var(--shadow-hard-sm)',
          display: 'grid', placeItems: 'center',
          fontFamily: 'var(--font-display)', fontSize: 10,
          color: 'var(--c-ink)',
        }}>★</div>
      </div>
    </div>
  );
}

const PinScatter = ({
  xAxis = 'X',
  yAxis = 'Y',
  xRange = { min: 0, max: 100 },
  yRange = { min: 0, max: 100 },
  myGuess,
  opponentPins = [],
  correctPosition,
  isInteractive = true,
  onPlace,
  staggerOpponents = false,
  topLabel = 'WHERE DOES THIS BATTER LAND?',
  topTitle = 'Drop your pin.',
  topBadge = 'CAREER',
  footerHint,
  embedded = false,
}) => {
  const { theme } = useTheme();
  const ref = useRef(null);
  const [revealCount, setRevealCount] = useState(staggerOpponents ? 0 : opponentPins.length);
  const [correctRevealed, setCorrectRevealed] = useState(!staggerOpponents);
  const [linesRevealed, setLinesRevealed] = useState(!staggerOpponents);

  useEffect(() => {
    if (!staggerOpponents) {
      setRevealCount(opponentPins.length);
      setCorrectRevealed(true);
      setLinesRevealed(true);
      return;
    }
    setRevealCount(0);
    setCorrectRevealed(false);
    setLinesRevealed(false);
    const timeouts = [];
    opponentPins.forEach((_, i) => {
      timeouts.push(setTimeout(() => setRevealCount((c) => Math.max(c, i + 1)), 300 * (i + 1)));
    });
    const correctDelay = 300 * Math.max(opponentPins.length, 1) + 350;
    timeouts.push(setTimeout(() => setCorrectRevealed(true), correctDelay));
    timeouts.push(setTimeout(() => setLinesRevealed(true), correctDelay + 700));
    return () => timeouts.forEach(clearTimeout);
  }, [opponentPins, staggerOpponents]);

  const handleTap = (e) => {
    if (!isInteractive || !onPlace) return;
    const rect = ref.current.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = (1 - (e.clientY - rect.top) / rect.height) * 100;
    onPlace({
      x: Math.max(2, Math.min(98, xPct)),
      y: Math.max(2, Math.min(98, yPct)),
    });
  };

  const xTicks = buildTicks(xRange.min, xRange.max, 4);
  const yTicks = buildTicks(yRange.min, yRange.max, 5);

  const visibleOpponents = staggerOpponents ? opponentPins.slice(0, revealCount) : opponentPins;
  const correctVisible = correctPosition && (!staggerOpponents || correctRevealed);

  // Lines from correct → every revealed pin (opponents + my pin)
  const lineTargets = correctPosition
    ? [
        ...visibleOpponents.map((p) => ({
          id: `opp-${p.id}`, x: p.x, y: p.y, color: p.color,
        })),
        ...(myGuess ? [{ id: '__me__', x: myGuess.x, y: myGuess.y, color: theme.yellow }] : []),
      ]
    : [];
  const distFor = (pin) => correctPosition
    ? Math.hypot(pin.x - correctPosition.x, pin.y - correctPosition.y)
    : 0;
  const colorForDist = (d) => (d <= 12 ? theme.green : d <= 28 ? theme.yellow : theme.pink);

  /* Identical padding in both modes so axis labels, ticks and quadrant text
     sit at exactly the same relative position in playing vs results views. */
  const rootStyle = {
    position: 'relative',
    width: '100%',
    ...(embedded ? { height: '100%' } : {}),
    padding: '12px 16px 44px 46px',  // top right bottom left — 44px clears x-axis chip (20px) + 4px gap + 20px footer line
    display: 'flex', flexDirection: 'column',
    boxSizing: 'border-box',
  };

  /* Same visual treatment in both modes — 1:1 aspect ratio for the plot area
     so the chart grid looks identical whether guessing or reviewing results.
     Embedded uses flex:1 so the card container controls height; standalone
     uses aspectRatio:'1/1' to force the same square shape. */
  const plotStyle = {
    position: 'relative',
    width: '100%',
    ...(embedded
      ? { flex: 1, minHeight: 0 }
      : { aspectRatio: '1 / 1' }),
    background: '#FFFBEF',
    border: 'var(--border-ink)',
    borderRadius: 12,
    boxShadow: embedded ? 'none' : 'var(--shadow-hard)',
    cursor: isInteractive ? 'crosshair' : 'default',
    overflow: 'visible',
    touchAction: 'manipulation',
      };

  return (
    <div style={rootStyle}>
      {/* title strip */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        marginBottom: 8, paddingRight: 4, gap: 8,
        flexShrink: 0,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 9, opacity: 0.7, letterSpacing: 0.6,
            color: 'var(--c-ink)',
          }}>{topLabel}</div>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 15, lineHeight: 1.05, marginTop: 2,
            color: 'var(--c-ink)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{topTitle}</div>
        </div>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700,
          padding: '3px 6px', background: 'var(--c-ink)', color: 'var(--c-yellow)', borderRadius: 5,
          flexShrink: 0,
        }}>{topBadge}</div>
      </div>

      {/* plot area */}
      <div
        ref={ref}
        onClick={handleTap}
        style={plotStyle}
      >
        <svg viewBox="0 0 100 100" preserveAspectRatio="none"
             style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          <defs>
            <radialGradient id={`pen-heat-${theme.pink.replace('#', '')}`} cx="70%" cy="25%" r="55%">
              <stop offset="0" stopColor={theme.pink} stopOpacity=".18" />
              <stop offset="1" stopColor={theme.pink} stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect x="0" y="0" width="100" height="100" fill={`url(#pen-heat-${theme.pink.replace('#', '')})`} />
          {[0.2, 0.4, 0.6, 0.8].map((t) => (
            <g key={t}>
              <line x1={t * 100} y1="0" x2={t * 100} y2="100" stroke="#16131520" strokeDasharray="2 3" strokeWidth=".4" />
              <line x1="0" y1={t * 100} x2="100" y2={t * 100} stroke="#16131520" strokeDasharray="2 3" strokeWidth=".4" />
            </g>
          ))}
        </svg>

        {/* Y tick chips */}
        {yTicks.slice().reverse().map((v, i, arr) => (
          <div key={`y-${i}`} style={{
            position: 'absolute',
            right: 'calc(100% + 4px)',
            top: `${(i / (arr.length - 1)) * 100}%`,
            transform: 'translateY(-50%)',
            fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 10,
            color: 'var(--c-ink)',
            background: 'var(--c-cream)',
            padding: '2px 4px', borderRadius: 4,
            border: '1px solid var(--c-ink)',
            lineHeight: 1,
          }}>{formatTick(v)}</div>
        ))}

        {/* X tick chips */}
        {xTicks.map((v, i, arr) => (
          <div key={`x-${i}`} style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: `${(i / (arr.length - 1)) * 100}%`,
            transform: 'translateX(-50%)',
            fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 10,
            color: 'var(--c-ink)', lineHeight: 1,
          }}>{formatTick(v)}</div>
        ))}

        {/* Y axis label — floats inside the plot's top-left corner instead of
            rotating in the left margin, since that margin is already tight
            with tick numbers (a 5-digit tick can eat nearly all of it) and a
            centered rotated label collided with whichever tick sat near the
            vertical middle. */}
        <div style={{
          position: 'absolute', top: 6, left: 6,
          display: 'flex', alignItems: 'center', gap: 4,
          fontFamily: 'var(--font-display)', fontSize: 10, letterSpacing: 0.4,
          pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
          <span style={{ fontSize: 9, opacity: 0.6, fontFamily: 'var(--font-mono)' }}>↑</span>
          <span style={{
            background: 'var(--c-ink)', color: 'var(--c-cream)',
            padding: '3px 6px', borderRadius: 6,
            border: '1.5px solid var(--c-ink)',
            boxShadow: '1.5px 1.5px 0 var(--c-ink)',
          }}>{(yAxis || 'Y').toString().toUpperCase()}</span>
        </div>

        {/* X axis label */}
        <div style={{
          position: 'absolute', bottom: -36, left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 4,
          fontFamily: 'var(--font-display)', fontSize: 10, letterSpacing: 0.4,
          pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>
          <span style={{
            background: 'var(--c-blue)', color: 'var(--c-cream)',
            padding: '3px 6px', borderRadius: 6,
            border: '1.5px solid var(--c-ink)',
            boxShadow: '1.5px 1.5px 0 var(--c-ink)',
          }}>{(xAxis || 'X').toString().toUpperCase()}</span>
          <span style={{ fontSize: 9, opacity: 0.6, fontFamily: 'var(--font-mono)' }}>→</span>
        </div>

        {/* connection lines: correct → each pin, drawn after correct lands */}
        {correctVisible && linesRevealed && lineTargets.length > 0 && (
          <svg viewBox="0 0 100 100" preserveAspectRatio="none"
               style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2 }}>
            {lineTargets.map((pin, i) => {
              const cx = correctPosition.x;
              const cy = 100 - correctPosition.y;
              const px = pin.x;
              const py = 100 - pin.y;
              const lineColor = colorForDist(distFor(pin));
              return (
                <line
                  key={pin.id}
                  x1={cx} y1={cy} x2={px} y2={py}
                  stroke={lineColor}
                  strokeWidth="2"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  pathLength="100"
                  strokeDasharray="100"
                  strokeDashoffset="100"
                  style={{
                    animation: `penDrawLine 620ms ${i * 110}ms var(--ease-snap) forwards`,
                  }}
                />
              );
            })}
          </svg>
        )}

        {/* distance chips at midpoint of each line */}
        {correctVisible && linesRevealed && lineTargets.map((pin, i) => {
          const midX = (pin.x + correctPosition.x) / 2;
          const midY = (pin.y + correctPosition.y) / 2;
          const d = distFor(pin);
          return (
            <div key={`chip-${pin.id}`} style={{
              position: 'absolute',
              left: `${midX}%`, top: `${100 - midY}%`,
              fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700,
              color: 'var(--c-ink)',
              background: colorForDist(d),
              border: 'var(--border-ink-sm)',
              borderRadius: 4,
              padding: '1px 5px',
              whiteSpace: 'nowrap',
              zIndex: 7,
              pointerEvents: 'none',
              opacity: 0,
              animation: `penChipPop 320ms ${i * 110 + 360}ms var(--ease-pop) forwards`,
            }}>{d.toFixed(1)}</div>
          );
        })}

        {correctVisible && <CorrectMarker x={correctPosition.x} y={correctPosition.y} />}

        {visibleOpponents.map((p) => (
          <OpponentPin key={p.id} pin={p} revealed={!!correctVisible} />
        ))}

        {myGuess && <MyPin x={myGuess.x} y={myGuess.y} locked={!isInteractive} correct={correctPosition} />}

        {isInteractive && myGuess && (
          <PinPreviewChip
            x={myGuess.x}
            y={myGuess.y}
            xLabel={xAxis}
            yLabel={yAxis}
            xValue={formatTick(xRange.min + (myGuess.x / 100) * (xRange.max - xRange.min))}
            yValue={formatTick(yRange.min + (myGuess.y / 100) * (yRange.max - yRange.min))}
          />
        )}
      </div>

      {/* footer hint */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: 42, padding: '0 2px', gap: 8,
      }}>
        <div style={{
          fontFamily: 'var(--font-mono)', fontWeight: 700,
          fontSize: 11, color: 'var(--c-ink)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          letterSpacing: 0.4,
        }}>
          {footerHint || (!isInteractive ? 'LOCKED IN' : myGuess ? 'TAP TO MOVE · LOCK BELOW' : 'TAP CHART TO PLACE PIN')}
        </div>
      </div>
    </div>
  );
};

export default PinScatter;
